import { Link } from "@/lib/router-compat";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";
import { motion } from "framer-motion";
import { ArrowDown, Play, Calendar, Clock, Globe, CircleDollarSign, Subtitles, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import CountdownTimer from "./CountdownTimer";
import type { WebinarMeta } from "@/lib/parseWebinarContent";
import { getDateLocale, uiT } from "@/i18n/ui";

interface WebinarHeroProps {
  title: string;
  subtitle?: string;
  summary: string | null;
  partnerLogo?: string;
  partnerName?: string;
  partnerUrl?: string;
  featuredImage: string | null;
  webinarDate: string | null;
  recordingUrl: string | null;
  registrationUrl: string | null;
  durationMinutes?: number | null;
  category?: string | null;
  serviceCategory?: string | null;
  tags?: string[] | null;
  meta?: WebinarMeta | null;
  isOnDemand?: boolean;
}

const WebinarHero = ({
  title,
  subtitle,
  summary,
  partnerLogo,
  partnerName,
  partnerUrl,
  featuredImage,
  webinarDate,
  recordingUrl,
  registrationUrl,
  durationMinutes,
  category,
  serviceCategory,
  tags,
  meta: parsedMeta,
  isOnDemand = false
}: WebinarHeroProps) => {
  const lang = useLanguage();
  const t = uiT(lang);
  const dateLoc = getDateLocale(lang);
  const isFuture = webinarDate ? new Date(webinarDate).getTime() > Date.now() : false;
  const isPast = webinarDate ? new Date(webinarDate).getTime() <= Date.now() : false;
  const localizeTag = (label: string) => {
    if (lang !== "zh-cn") return label;
    const labels: Record<string, string> = {
      Clinical: "临床",
      "In Vitro Diagnostics": "体外诊断",
      "WHO Prequalification": "WHO 预认证",
      "Infectious Disease": "传染病",
    };
    return labels[label] || label;
  };

  const scrollToForm = () => {
    document.getElementById("register-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToContent = () => {
    document.getElementById("webinar-content")?.scrollIntoView({ behavior: "smooth" });
  };

  // Render inline bold markdown (**) as <strong> and link the partner name; leave everything else as text.
  const renderMarkdownBold = (text: string): React.ReactNode => {
    const parts = text.split("**");
    return parts.map((part, index) =>
      index % 2 === 1
        ? <strong key={index}>{part}</strong>
        : <span key={index}>{linkifyPartner(part)}</span>
    );
  };

  const linkifyPartner = (text: string): React.ReactNode => {
    if (!partnerName || !partnerUrl) return text;
    const at = text.indexOf(partnerName);
    if (at === -1) return text;
    return (
      <>
        {text.slice(0, at)}
        <a
          href={partnerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-foreground underline underline-offset-4 decoration-1 hover:decoration-2 transition-colors">
          {partnerName}
        </a>
        {linkifyPartner(text.slice(at + partnerName.length))}
      </>
    );
  };

  return (
    <section className="relative min-h-[540px] lg:min-h-[600px] flex items-end overflow-hidden">
      {featuredImage ?
      <img
        src={featuredImage}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover" /> :


      <div className="absolute inset-0 bg-primary" />
      }
      <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/88 to-primary/40" />

      <div className="relative z-10 container mx-auto px-6 lg:px-10 max-w-5xl pb-14 pt-44">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}>
          
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-primary-foreground/50">
              <li><Link to={localizeHref("/", lang)} className="hover:text-primary-foreground/80 transition-colors">{t("home")}</Link></li>
              <li aria-hidden="true">/</li>
              <li><Link to={localizeHref("/en/knowledge-center?type=webinars", lang)} className="hover:text-primary-foreground/80 transition-colors">{t("webinars")}</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-primary-foreground/70 max-w-[36rem] sm:truncate">{title}</li>
            </ol>
          </nav>

          {/* Badge */}
          <span className="inline-block text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-5 bg-accent text-accent-foreground backdrop-blur-sm">
            {isOnDemand || isPast ?
            t("onDemandWebinar") :
            isFuture ?
            t("upcomingWebinar") :
            t("onDemandWebinar")
            }
          </span>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground tracking-tight leading-[1.1] max-w-3xl">
            {title}
          </h1>
          {subtitle && (
            <p className="text-xl sm:text-2xl lg:text-3xl font-semibold text-primary-foreground/75 tracking-tight max-w-3xl leading-[1.2]">
              {subtitle}
            </p>
          )}

          {summary &&
          <p className="mt-2 text-base sm:text-lg text-primary-foreground/65 leading-relaxed max-w-3xl whitespace-pre-line">
              {renderMarkdownBold(summary)}
            </p>
          }

          {partnerLogo && (
            <img
              src={partnerLogo}
              alt="KALMS Consulting"
              className="mt-5 w-[260px] max-w-full h-auto rounded-lg bg-primary-foreground/30 p-3"
            />
          )}

          {/* Tag pills */}
          {(() => {
            const pillLabels = Array.from(
              new Set([category, ...(tags ?? [])].filter(Boolean) as string[])
            )
              .filter((label) => label !== "Clinical Solutions" && !label.startsWith("_"))
              .slice(0, 6);
            return pillLabels.length > 0 ?
            <div className="flex flex-wrap items-center gap-2 mt-6">
                {pillLabels.map((label) =>
              <Link
                key={label}
                to={localizeHref(`/en/knowledge-center?filter=${encodeURIComponent(label)}`, lang)}
                className="text-[11px] font-bold bg-primary-foreground/15 text-primary-foreground/90 px-3.5 py-1.5 rounded-full uppercase tracking-wider backdrop-blur-sm border border-primary-foreground/10 hover:bg-primary-foreground/25 transition-colors">

                    {localizeTag(label)}
                  </Link>
              )}
              </div> :
            null;
          })()}

          {/* CTAs */}
          



















          

          {/* Social proof */}
          {isFuture



          }

          {/* Meta bar */}
          {(() => {
            const showOnDemand = isOnDemand || isPast;
            const formattedDate = showOnDemand ?
            lang === "es" ? "Bajo demanda" : lang === "zh-cn" ? "随时观看" : "On Demand" :
            webinarDate ?
            new Date(webinarDate).toLocaleDateString(dateLoc, {
              weekday: "long", month: "long", day: "numeric", year: "numeric"
            }) :
            parsedMeta?.date || "TBD";
            const timeLabel = showOnDemand ?
            durationMinutes ? `${durationMinutes} ${t("min")}` : parsedMeta?.time || "1h" :
            parsedMeta?.time || (durationMinutes ? `${durationMinutes} ${t("min")}` : "TBD");
            const serviceTags = Array.from(new Set([serviceCategory, category].filter(Boolean) as string[])).filter(
              (tag) => tag !== "Clinical Solutions"
            );
            const industryLabel = serviceTags.length > 0
              ? serviceTags.map(localizeTag).join(" · ")
              : parsedMeta?.industry || (lang === "zh-cn" ? "生命科学" : "Life Sciences");
            const langLabel = lang === "zh-cn" ? "中文" : "English";
            const rawSubtitleTag = (tags ?? []).find((t) => t && /subtitle/i.test(t));
            const subtitleTag = rawSubtitleTag
              ? lang === "zh-cn"
                ? "简体中文字幕"
                : lang === "es"
                ? "Subtítulos en chino simplificado"
                : rawSubtitleTag
              : null;
            const metaItems = [
            { icon: Calendar, label: formattedDate },
            { icon: Clock, label: timeLabel },
            ...(serviceTags.length > 0 ? [{ icon: Tag, label: industryLabel }] : []),
            { icon: Globe, label: langLabel },
            ...(subtitleTag ? [{ icon: Subtitles, label: subtitleTag }] : [])];

            return (
              <div className="mt-8 pt-6 border-t border-primary-foreground/15 flex flex-wrap items-center gap-x-8 gap-y-3">
                {metaItems.map((item, i) =>
                <div key={i} className="flex items-center gap-2.5 text-sm text-primary-foreground/60">
                    <item.icon size={18} strokeWidth={1.6} className="text-accent shrink-0" />
                    <span>{item.label}</span>
                  </div>
                )}
              </div>);

          })()}

        </motion.div>
      </div>
    </section>);

};

export default WebinarHero;